import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { GlobalStateEnum} from '@/config';
import { CommonUtils } from '@/utils/commonUtils';
import { newRouterGroupApi } from '@/api/new-router-group/new-router-group-api';
import type { RouterGroupType } from '@/api/new-router-group/type';
import { cloneDeep, isUrl } from '@pureadmin/utils';
import { constantRoutes, constantCommonRoutes, remainingRoutes } from './modules';
import Layout from '@/layout/Main.vue';
const modulesRoutes = import.meta.glob('/src/views/**/*.{vue,tsx}');
// 查找本地组件
export const findLocalComponent = (path: string) => {
  const modulesRoutesKeys = Object.keys(modulesRoutes);
  // 移除开头的斜杠，并确保路径以 /index.vue 结尾
  const normalizedPath = path.replace(/^\//, '') + '/index.vue';
  // 精确匹配路径
  const exactMatch = modulesRoutesKeys.find((key) =>
    key.toLowerCase().includes(normalizedPath.toLowerCase())
  );

  if (exactMatch) {
    return modulesRoutes[exactMatch];
  }

  // 如果没有找到精确匹配，尝试查找目录
  const dirMatch = modulesRoutesKeys.find((key) => key.toLowerCase().includes(path.toLowerCase()));

  return dirMatch ? modulesRoutes[dirMatch] : undefined;
};
// 处理动态路由
export const handleAsyncRoutes = (data: RouterGroupType[]) => {
  const filteredGroups = data
    .filter((group) => !group.path.startsWith('/workspace') && !group.path.startsWith('http'))
    .map((group) => ({
      ...group,
      routers: group.routers?.filter(
        (child) => !child.path.startsWith('/workspace') && !child.path.startsWith('http')
      ),
    }));
  const addedRouteNames = new Set<string>();
  filteredGroups.forEach((group) => {
    const parentName = group.path.replace(/^\//, '').replace(/\//g, '-');
    const hasChildren = group.routers && group.routers.length > 0;
    const parentRoute: RouteRecordRaw = {
      path: group.path,
      name: parentName,
      component: Layout,
      meta: {
        title: group.name,
        rank: group.rank,
        icon: group.icon,
      },
      children: [],
    };
    if (hasChildren) {
      parentRoute.children = group.routers.map((child, index) => {
        const childName = `${parentName}-child-${index}`;
        const component = findLocalComponent(child.path);
        return {
          path: child.path, // 空路径继承父路径
          name: childName,
          component: component || (() => import('@/views/error/404.vue')),
          meta: {
            isExternalLink: child.isExternalLink === GlobalStateEnum.Success,
            title: child.title,
            icon: child.icon,
            showLink: group.status === GlobalStateEnum.Success,
            rank: child.rank,
          },
        };
      });
    }

    if (!addedRouteNames.has(parentName)) {
      router.addRoute(parentRoute);
      addedRouteNames.add(parentName);
    }
  });
};
// 创建路由实例
const router = createRouter({
  history: createWebHistory(),
  routes: [...constantRoutes, ...constantCommonRoutes, ...remainingRoutes],
});

// 初始化路由
export const initRouter = async () => {
  try {
    console.log('初始化：从服务器获取路由');
    const { data } = await newRouterGroupApi.getNewRouterGroup();
    if (data && data.length > 0) {
      handleAsyncRoutes(cloneDeep(data));
    } else {
      console.error('初始化：获取到的路由数据为空');
    }
  } catch (error) {
    console.error('初始化路由失败:', error);
  }
};

// 路由标题设置
router.beforeEach(async (to, from, next) => {
  const host = window.location.host;
  const testHostWorkspace = import.meta.env.VITE_TEST_HOST_WORKSPACE;
  // 判断是否为内网
  const isLocalhost = ['192.168', 'localhost', '127.0.0.1'].some((ip) => host.includes(ip));
  // 登录路径拦截逻辑
  if (to.path.startsWith('/login')) {
    const parts = to.path.split('/').filter(Boolean); // 移除空字符串
    const inviteCode = parts.length >= 2 ? parts[1] : null; // login 后面第一个就是邀请码
    const base = isLocalhost ? testHostWorkspace : host;
    const redirectUrl = inviteCode
      ? `https://${base}/workspace/welcome?inviteCode=${inviteCode}`
      : `https://${base}/workspace/welcome`;
    window.location.href = redirectUrl;
    return; // 阻止继续路由流程
  }
  // 设置标题
  const externalLink = isUrl(to?.name as string);
  if (!externalLink) {
    to.matched.some((item) => {
      if (!item.meta.title) return '';
      const title = item.meta.title;
      document.title = CommonUtils.transformI18n(title);
    });
  }
  if (!document.title) {
    document.title = '默认标题';
  }
  // 如果是刷新页面（from.name 为空）且不是欢迎页面
  if (!from.name && to.path !== '/welcome') {
    try {
      // 直接从服务器获取路由
      console.log('刷新：从服务器获取路由');
      const { data } = await newRouterGroupApi.getNewRouterGroup();
      if (data && data.length > 0) {
        handleAsyncRoutes(cloneDeep(data));
        // 检查目标路由是否存在
        if (router.hasRoute(to.name as string)) {
          next();
        } else {
          next('/welcome');
        }
        return;
      }
    } catch (error) {
      console.error('刷新时加载路由失败:', error);
      next('/welcome');
      return;
    }
  }

  // 正常导航
  next();
});

export default router;
